import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/use-toast';

export interface ArticleAvitaillement {
  nom: string;
  categorie: string;
  quantite: number;
  unite: string;
  priorite: 'normale' | 'critique';
}

export interface ListeAvitaillement {
  id: string;
  user_id: string;
  bateau_id: string | null;
  nom: string;
  articles: ArticleAvitaillement[];
  nombre_personnes: number;
  nombre_jours: number;
  est_favori: boolean;
  created_at: string;
  updated_at: string;
}

export const useListesAvitaillement = () => {
  const [listes, setListes] = useState<ListeAvitaillement[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchListes = useCallback(async () => {
    if (!user) {
      setListes([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('listes_avitaillement')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors du chargement des listes:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les listes d'avitaillement.",
          variant: "destructive",
        });
        setListes([]);
      } else {
        const listesTyped: ListeAvitaillement[] = (data || []).map(item => ({
          ...item,
          articles: Array.isArray(item.articles) ? (item.articles as unknown as ArticleAvitaillement[]) : []
        }));
        setListes(listesTyped);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setListes([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const creerListe = async (
    nom: string,
    bateauId: string | null,
    articles: ArticleAvitaillement[],
    nombrePersonnes: number,
    nombreJours: number
  ) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { data, error } = await supabase
        .from('listes_avitaillement')
        .insert({
          user_id: user.id,
          bateau_id: bateauId,
          nom,
          articles: articles as any,
          nombre_personnes: nombrePersonnes,
          nombre_jours: nombreJours
        })
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la création de la liste:', error);
        toast({
          title: "Erreur",
          description: "Impossible de créer la liste.",
          variant: "destructive",
        });
        return { error };
      } else {
        await fetchListes();
        toast({
          title: "Succès",
          description: "Liste d'avitaillement créée avec succès !",
        });
        return { data };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const modifierListe = async (id: string, updates: Partial<ListeAvitaillement>) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const updateData: any = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      // Convert articles to proper format if present
      if (updates.articles) {
        updateData.articles = updates.articles as any;
      }

      const { data, error } = await supabase
        .from('listes_avitaillement')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la modification de la liste:', error);
        toast({
          title: "Erreur",
          description: "Impossible de modifier la liste.",
          variant: "destructive",
        });
        return { error };
      } else {
        await fetchListes();
        toast({
          title: "Succès",
          description: "Liste modifiée avec succès !",
        });
        return { data };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const supprimerListe = async (id: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { error } = await supabase
        .from('listes_avitaillement')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Erreur lors de la suppression:', error);
        toast({
          title: "Erreur",
          description: "Impossible de supprimer la liste.",
          variant: "destructive",
        });
        return { error };
      } else {
        await fetchListes();
        toast({
          title: "Succès",
          description: "Liste supprimée avec succès !",
        });
        return { success: true };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const dupliquerListe = async (id: string) => {
    const listeOriginale = listes.find(l => l.id === id);
    if (!listeOriginale) return { error: 'Liste non trouvée' };

    return creerListe(
      `${listeOriginale.nom} (Copie)`,
      listeOriginale.bateau_id,
      listeOriginale.articles,
      listeOriginale.nombre_personnes,
      listeOriginale.nombre_jours
    );
  };

  const toggleFavori = async (id: string) => {
    const liste = listes.find(l => l.id === id);
    if (!liste) return { error: 'Liste non trouvée' };

    return modifierListe(id, { est_favori: !liste.est_favori });
  };

  const creerArticlesInventaire = async (listeId: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    const liste = listes.find(l => l.id === listeId);
    if (!liste) return { error: 'Liste non trouvée' };

    try {
      // Créer les articles dans l'inventaire avec quantité à 0
      const articlesInventaire = liste.articles.map(article => ({
        user_id: user.id,
        bateau_id: liste.bateau_id,
        nom: article.nom,
        categorie: article.categorie,
        sous_categorie: assignSousCategorie(article.nom),
        quantite: 0,
        quantite_max: Math.ceil(article.quantite) // Arrondir à l'entier supérieur
      }));

      console.log('Création des articles inventaire:', articlesInventaire.length);

      const { data, error } = await supabase
        .from('inventaire')
        .insert(articlesInventaire)
        .select();

      if (error) {
        console.error('Erreur lors de la création des articles:', error);
        toast({
          title: "Erreur",
          description: "Impossible de créer les articles dans l'inventaire.",
          variant: "destructive",
        });
        return { error };
      } else {
        console.log('Articles créés dans l\'inventaire:', data?.length || 0);
        toast({
          title: "Succès",
          description: `${data?.length || 0} article(s) créé(s) dans l'inventaire avec stock à zéro.`,
        });
        return { success: true, count: data?.length || 0 };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  useEffect(() => {
    if (user) {
      fetchListes();
    }
  }, [user, fetchListes]);

  return {
    listes,
    loading,
    creerListe,
    modifierListe,
    supprimerListe,
    dupliquerListe,
    toggleFavori,
    creerArticlesInventaire,
    refetchListes: fetchListes
  };
};

function assignSousCategorie(nom: string): string | null {
  const nomLower = nom.toLowerCase();
  
  // Conserves
  if (nomLower.includes('conserve') || nomLower.includes('boîte') || nomLower.includes('thon') || nomLower.includes('sardine')) {
    return 'Conserves';
  }
  
  // Pâtes et riz
  if (nomLower.includes('pâte') || nomLower.includes('spaghetti') || nomLower.includes('macaroni')) {
    return 'Pâtes';
  }
  if (nomLower.includes('riz')) {
    return 'Riz';
  }
  
  // Légumes frais
  if (nomLower.includes('tomate') || nomLower.includes('courgette') || nomLower.includes('carotte') || 
      nomLower.includes('oignon') || nomLower.includes('poivron') || nomLower.includes('concombre') || 
      nomLower.includes('laitue') || nomLower.includes('pomme de terre')) {
    return 'Légumes frais';
  }
  
  // Fruits frais
  if (nomLower.includes('pomme') || nomLower.includes('banane') || nomLower.includes('orange') ||
      nomLower.includes('poire') || nomLower.includes('pêche') || nomLower.includes('melon')) {
    return 'Fruits frais';
  }
  
  // Produits laitiers
  if (nomLower.includes('lait') || nomLower.includes('fromage') || nomLower.includes('yaourt') || nomLower.includes('beurre')) {
    return 'Produits laitiers';
  }
  
  // Boissons
  if (nomLower.includes('eau') || nomLower.includes('jus') || nomLower.includes('soda')) {
    return 'Boissons';
  }
  
  // Pain et céréales
  if (nomLower.includes('pain') || nomLower.includes('céréale') || nomLower.includes('muesli')) {
    return 'Pain et céréales';
  }
  
  return null;
}
